// apps/admin/src/features/billing/TransferGroupHistoryDialog.tsx
//
// D-70 (amend #3 → close) — G-63 admin transfer history + CSV export.
// Surfaces the existing BE endpoint that had no UI:
//   GET /api/v1/admin/transfer-groups/:id/history?sender=&receiver=&date_from=
//       &date_to=&amount_min=&amount_max=&cursor=&limit=&export=csv
// This dialog lists a group's routed TRANSFER_SENT tx_groups and offers a
// full-buffer CSV export (opens in a new tab via resolveApiBaseUrl()).
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  formatUsdCents,
} from '@printway/ui';
import { apiClient, resolveApiBaseUrl } from '@printway/shared/api';

import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

interface HistoryRow {
  tx_group_id: string;
  sender_merchant_id: string;
  recipient_merchant_id: string;
  /** Owner emails resolved BE-side (dbg-20260723) — shown instead of ObjectIds. */
  sender_email?: string | null;
  recipient_email?: string | null;
  amount_usd_cents: number;
  note: string | null;
  created_at: string;
}

interface RawClient {
  GET: (
    path: string,
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

export interface TransferGroupHistoryDialogProps {
  group: { id: string; name: string } | null;
  onClose: () => void;
}

export function TransferGroupHistoryDialog(
  props: TransferGroupHistoryDialogProps,
): JSX.Element {
  const { group, onClose } = props;
  const { t } = useTranslation('finance');
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Numbered pagination (newest-first BE sort).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const load = useCallback(
    async (groupId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const client = apiClient() as unknown as RawClient;
        const { data, error: err, response } = await client.GET(
          `/api/v1/admin/transfer-groups/${groupId}/history?page=${page}&page_size=${pageSize}`,
        );
        if (err || !response.ok) {
          setError(t('errors.requestFailed', { status: response.status }));
          setRows([]);
          return;
        }
        const payload = data as
          | { data?: { data?: HistoryRow[] } | HistoryRow[]; total?: number }
          | undefined;
        const inner = (payload?.data as { data?: HistoryRow[] })?.data ??
          (payload?.data as HistoryRow[] | undefined) ??
          [];
        const arr = Array.isArray(inner) ? inner : [];
        setRows(arr);
        setTotal(typeof payload?.total === 'number' ? payload.total : arr.length);
      } catch {
        setError(t('errors.requestFailed', { status: 0 }));
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [t, page, pageSize],
  );

  useEffect(() => {
    if (group?.id) void load(group.id);
  }, [group?.id, load]);

  // Reset to page 1 whenever a different group opens.
  useEffect(() => {
    setPage(1);
    setTotal(0);
  }, [group?.id]);

  const handleExportCsv = useCallback((): void => {
    if (!group?.id) return;
    const url = `${resolveApiBaseUrl()}/api/v1/admin/transfer-groups/${group.id}/history?export=csv`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [group?.id]);

  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>
          {t('transfer_group.history.title', { name: group?.name ?? '' })}
        </DialogTitle>
      </DialogHeader>

      <div
        className="max-h-[55vh] overflow-auto"
        data-testid="transfer-group-history-body"
      >
        {loading ? (
          <p className="px-1 py-4 text-sm text-pw-text-muted">
            {t('transfer_group.history.loading')}
          </p>
        ) : error ? (
          <p className="px-1 py-4 text-sm text-rose-600 dark:text-rose-300">{error}</p>
        ) : rows.length === 0 ? (
          <p
            className="px-1 py-4 text-sm text-pw-text-muted"
            data-testid="transfer-group-history-empty"
          >
            {t('transfer_group.history.empty')}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-pw-text-muted">
                <th className="px-2 py-2">{t('transfer_group.history.date')}</th>
                <th className="px-2 py-2">{t('transfer_group.history.sender')}</th>
                <th className="px-2 py-2">
                  {t('transfer_group.history.recipient')}
                </th>
                <th className="px-2 py-2 text-right">
                  {t('transfer_group.history.amount')}
                </th>
                <th className="px-2 py-2">{t('transfer_group.history.note')}</th>
              </tr>
            </thead>
            <tbody data-testid="transfer-group-history-rows">
              {rows.map((r) => (
                <tr key={r.tx_group_id} className="border-t border-pw-border">
                  <td className="px-2 py-2 text-pw-text-muted">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td
                    className="max-w-[180px] truncate px-2 py-2 text-[12px]"
                    title={r.sender_email ?? r.sender_merchant_id}
                  >
                    {r.sender_email ?? (r.sender_merchant_id.slice(-8) || '—')}
                  </td>
                  <td
                    className="max-w-[180px] truncate px-2 py-2 text-[12px]"
                    title={r.recipient_email ?? r.recipient_merchant_id}
                  >
                    {r.recipient_email ?? (r.recipient_merchant_id.slice(-8) || '—')}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {formatUsdCents(r.amount_usd_cents)}
                  </td>
                  <td className="px-2 py-2 text-pw-text-muted">
                    {r.note ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 ? (
        <div className="border-t border-pw-border px-1 py-2">
          <PageListFooter
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={Math.max(1, Math.ceil(total / pageSize))}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            testId="tg-history-dialog-footer"
          />
        </div>
      ) : null}

      <DialogFooter>
        <Button
          variant="outline"
          onClick={handleExportCsv}
          disabled={loading || rows.length === 0}
          data-testid="transfer-group-history-export-csv"
        >
          {t('transfer_group.history.exportCsv')}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          {t('transfer_group.history.close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default TransferGroupHistoryDialog;
